import React, { useState, useEffect, useCallback } from "react";
import { Save, Copy, Zap, ShieldCheck, Globe, HelpCircle, Trash2 } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const ZendeskConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [zendeskConfig, setZendeskConfig] = useState({
    subdomain: "",
    email: "",
    api_token: "",
    is_active: false,
    streaming_enabled: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/zendesk/config", {
        aiAgentId: activeAiAgentId
      });
      if (response.success && response.config) {
        setZendeskConfig({
          subdomain: response.config.subdomain || "",
          email: response.config.email || "",
          api_token: response.config.api_token ? UNCHANGED_KEY : "",
          is_active: response.config.is_active || false,
          streaming_enabled: response.config.streaming_enabled || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.error("Failed to load Zendesk config:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!zendeskConfig.subdomain.trim()) {
      showToast("Please enter your Zendesk subdomain", "error");
      return;
    }

    if (!zendeskConfig.email.trim()) {
      showToast("Please enter your Zendesk agent email", "error");
      return;
    }

    if (!zendeskConfig.api_token.trim()) {
      showToast("Please enter your API token", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify(zendeskConfig),
        aiAgentId: activeAiAgentId
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Zendesk configuration saved successfully!", "success");
        // Update local state to mask API token if it was new
        if (zendeskConfig.api_token !== UNCHANGED_KEY) {
          setZendeskConfig(prev => ({
            ...prev,
            api_token: UNCHANGED_KEY
          }));
        }
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/zendesk", {
        method: "DELETE"
      });
      if (response.success) {
        setZendeskConfig({
          subdomain: "",
          email: "",
          api_token: "",
          is_active: false,
          streaming_enabled: false,
        });
        setWebhookUrl("");
        showToast("Zendesk integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith('http://') || webhookUrl.startsWith('https://')) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith('/') ? path : '/' + path;
    if (cleanBase.endsWith('/api') && cleanPath.startsWith('/api')) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!zendeskConfig.is_active) {
      if (!zendeskConfig.subdomain?.trim() || !zendeskConfig.email?.trim() || !zendeskConfig.api_token?.trim()) {
        showToast("Please enter and save subdomain, email, and API token first", "error");
        return;
      }
      if (zendeskConfig.api_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !zendeskConfig.is_active;
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
        aiAgentId: activeAiAgentId
      });

      if (response.success) {
        setZendeskConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(newActiveState ? "Zendesk integration activated!" : "Zendesk integration deactivated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const toggleStreaming = async () => {
    try {
      const newStreamingState = !zendeskConfig.streaming_enabled;
      const response = await ApiClient.request("/zendesk/config", {
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: newStreamingState }),
        aiAgentId: activeAiAgentId
      });

      if (response.success) {
        setZendeskConfig((prev) => ({ ...prev, streaming_enabled: newStreamingState }));
        showToast("Streaming status updated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader 
          title="Zendesk Integration"
          description="Sync AI agent intelligence directly with your Zendesk Support Ticket system."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${zendeskConfig.is_active ? "text-[#83bbe1]" : "text-gray-300"}`}>
                {zendeskConfig.is_active ? "Operational" : "Offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={zendeskConfig.is_active}
                  onChange={toggleActive}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Main Configuration Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center text-white">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Zendesk API Access</h3>
                <p className="text-[11px] font-medium text-gray-400">Securely connect your Zendesk account</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Zendesk Subdomain */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Zendesk Subdomain
                </label>
                <div className="relative group flex items-center bg-gray-50 rounded-2xl focus-within:ring-2 focus-within:ring-gray-900 transition-all">
                  <input
                    type="text"
                    value={zendeskConfig.subdomain}
                    onChange={(e) =>
                      setZendeskConfig({
                        ...zendeskConfig,
                        subdomain: e.target.value,
                      })
                    }
                    placeholder="your-subdomain"
                    className="flex-1 px-5 py-4 bg-transparent border-0 text-gray-900 placeholder:text-gray-400 text-sm focus:outline-none"
                  />
                  <span className="px-5 text-sm font-semibold text-gray-400 border-l border-gray-200">
                    .zendesk.com
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  If your Zendesk URL is <code>https://mycompany.zendesk.com</code>, enter <code>mycompany</code>.
                </p>
              </div>

              {/* Agent Email */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  Admin/Agent Email
                </label>
                <div className="relative group">
                  <input
                    type="email"
                    value={zendeskConfig.email}
                    onChange={(e) =>
                      setZendeskConfig({
                        ...zendeskConfig,
                        email: e.target.value,
                      })
                    }
                    placeholder="agent@company.com"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 text-sm transition-all"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  The email address associated with the API Token.
                </p>
              </div>

              {/* API Token */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700 ml-1">
                  API Token
                </label>
                <div className="relative group">
                  <input
                    type="password"
                    value={zendeskConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : zendeskConfig.api_token}
                    onChange={(e) =>
                      setZendeskConfig({
                        ...zendeskConfig,
                        api_token: e.target.value,
                      })
                    }
                    onFocus={(e) => {
                      if (zendeskConfig.api_token === UNCHANGED_KEY) {
                        setZendeskConfig({ ...zendeskConfig, api_token: "" });
                      }
                    }}
                    placeholder={zendeskConfig.api_token === UNCHANGED_KEY ? MASK_DISPLAY : "Your Zendesk API Token"}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity">
                    <Zap size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 ml-1">
                  Generate this in <span className="font-bold">Admin Center → Apps and Integrations → APIs → Zendesk API</span>. Make sure Token Access is enabled.
                </p>
              </div>

              {/* Streaming Responses Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-gray-900">Streaming Responses</h4>
                  <p className="text-[11px] font-medium text-gray-400">Stream response tokens to Zendesk tickets (as comments are being drafted).</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={zendeskConfig.streaming_enabled}
                    onChange={toggleStreaming}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-gray-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-gray-900"></div>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-50 flex justify-between items-center">
                {zendeskConfig.subdomain && (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 border border-red-200 text-red-600 rounded-2xl hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm"
                  >
                    <Trash2 size={18} />
                    Delete Integration
                  </button>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving || !zendeskConfig.subdomain?.trim() || !zendeskConfig.email?.trim() || !zendeskConfig.api_token?.trim()}
                  className="flex items-center gap-2 px-8 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-lg shadow-gray-200 text-sm ml-auto"
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Zendesk Config"}
                </button>
              </div>
            </div>
          </div>

          {/* Webhook Card */}
          {webhookUrl && (
            <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 animate-slideUp">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                  <Globe size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Webhook Delivery</h3>
                  <p className="text-[11px] font-medium text-gray-400">Receive real-time ticket updates from Zendesk</p>
                </div>
              </div>

              <div className="p-5 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group transition-all hover:border-blue-200 hover:bg-blue-50/10">
                <label className="block mb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Live Webhook URL
                </label>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-mono text-xs text-gray-600 truncate flex-1 leading-loose">
                    {displayUrl}
                  </div>
                  <button
                    onClick={copyWebhookUrl}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-900 hover:text-white rounded-xl text-[10px] font-bold text-gray-500 transition-all border border-gray-100 shadow-sm"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-1 bg-[#FDFCFD] rounded-lg mt-0.5 border border-gray-100">
                    <HelpCircle size={14} className="text-gray-400" />
                  </div>
                  <div className="text-xs text-gray-500 space-y-2">
                    <p className="font-bold text-gray-700">How to configure Zendesk webhooks:</p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>In Zendesk Admin Center, go to <strong>Apps and Integrations → Webhooks</strong> and click <strong>Create Webhook</strong>.</li>
                      <li>Select <strong>Trigger</strong> as the webhook type, and paste the Live Webhook URL above.</li>
                      <li>Set Request Method to <strong>POST</strong> and Format to <strong>JSON</strong>.</li>
                      <li>Go to <strong>Objects and Rules → Triggers</strong> and create a Trigger to notify this webhook when a ticket is created or updated.</li>
                      <li>Configure the Trigger action payload with the following JSON:</li>
                    </ol>
                    <pre className="p-3 bg-gray-900 text-gray-100 font-mono text-[10px] rounded-xl overflow-x-auto">
{`{
  "ticket_id": "{{ticket.id}}",
  "latest_comment": "{{ticket.latest_comment}}",
  "comment_author_id": "{{ticket.latest_comment.author.id}}"
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Delete Zendesk Integration"
        message="Are you sure you want to delete this integration config? This will remove all credentials and disable the webhook."
        confirmText="Delete Integration"
        isLoading={saving}
      />
    </div>
  );
};

export default ZendeskConfig;
